import * as React from "react"

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { type VariantProps } from "class-variance-authority"
import { ChevronDown } from "lucide-react"

import { cn } from "../../cn"
import { useDecorativeSvgs } from "../../hooks/useDecorativeSvgs"
import { buttonVariants, type ButtonTooltipProps } from "../Button/Button"
import { IconSlot } from "../Button/IconSlot"
import {
  dropdownMenuContentVariants,
  DropdownMenuItem,
} from "../DropdownMenu/DropdownMenu"
import { Loader } from "../Loader/Loader"
import { renderedText, Tooltip } from "../Tooltip/Tooltip"

const DIVIDER_STYLE: Record<string, React.CSSProperties> = {
  default: { backgroundColor: "rgba(255, 255, 255, 0.3)" },
  secondary: { backgroundColor: "var(--coval-border-active)" },
  ghost: { backgroundColor: "var(--coval-border)" },
  danger: { backgroundColor: "var(--coval-danger-border)", opacity: 0.3 },
}

export interface SplitButtonItem {
  label: string
  onClick: () => void
  icon?: React.ReactNode
  disabled?: boolean
  destructive?: boolean
  "data-testid"?: string
}

export interface SplitButtonProps extends VariantProps<typeof buttonVariants> {
  children: React.ReactNode
  onClick: () => void
  items: SplitButtonItem[]
  disabled?: boolean
  /**
   * Marks the primary action as in flight: disables both segments, sets
   * `aria-busy`, and prepends a spinner to the primary label.
   */
  loading?: boolean
  /**
   * Why the whole control is unavailable. A split button is disabled as a
   * whole, so given a reason the pair is wrapped in a tooltip that stands in
   * for it: one hoverable, focusable element speaks for both segments rather
   * than one per segment. Without a reason the chevron still stands in for
   * itself under its own label, so a disabled control is never left silent.
   */
  disabledReason?: React.ReactNode
  /**
   * Forwarded to whichever tooltip the control shows — the chevron's own
   * label as much as `disabledReason` — since the knobs that place one
   * place the other.
   */
  tooltipProps?: ButtonTooltipProps
  className?: string
  "aria-label"?: string
  "data-testid"?: string
}

const SplitButton = React.forwardRef<HTMLButtonElement, SplitButtonProps>(
  (
    {
      children,
      onClick,
      items,
      variant,
      disabled,
      loading,
      disabledReason,
      tooltipProps,
      className,
      "aria-label": ariaLabel,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    const isDisabled = Boolean(disabled || loading)
    const setPrimaryRef = useDecorativeSvgs(ref)
    /**
     * A reason only counts if there is something to read: a caller narrowing
     * nullable data down to `null` is saying it has nothing to say, and the
     * chevron should keep explaining itself rather than open an empty tip.
     */
    const explainsItself = isDisabled && Boolean(disabledReason)
    /**
     * The stand-in for the pair is named for the primary action, falling back
     * to the label the caller gave the control. Without this the name would
     * have to be read off the wrapper, which holds no words of its own once
     * the primary renders an icon or a component. It always resolves to
     * something, since a stand-in with no name is worse than a plain one.
     */
    const pairLabel = renderedText(children) ?? ariaLabel ?? "More options"

    const chevron = (
      <DropdownMenuPrimitive.Trigger asChild disabled={isDisabled}>
        <button
          type="button"
          aria-label={ariaLabel ?? "More options"}
          disabled={isDisabled}
          data-testid={dataTestId ? `${dataTestId}-options` : undefined}
          className={cn(
            buttonVariants({ variant }),
            "rounded-l-none px-1.5 focus-visible:z-10",
          )}
        >
          <ChevronDown className="size-3.5" aria-hidden="true" />
        </button>
      </DropdownMenuPrimitive.Trigger>
    )

    const control = (
      <div
        className={cn("inline-flex", className)}
        aria-label={explainsItself ? pairLabel : undefined}
        // The stand-in reads this off the pair, not off the primary it hides.
        aria-busy={(explainsItself && loading) || undefined}
      >
        <button
          ref={setPrimaryRef}
          type="button"
          onClick={onClick}
          disabled={isDisabled}
          aria-busy={loading || undefined}
          data-testid={dataTestId}
          className={cn(
            buttonVariants({ variant }),
            "rounded-r-none focus-visible:z-10",
          )}
        >
          {loading && <Loader size="sm" className="size-3.5 text-current" />}
          {children}
        </button>
        <div
          className="w-px self-stretch shrink-0"
          style={DIVIDER_STYLE[variant ?? "default"]}
          aria-hidden="true"
        />
        {/* The pair's own tooltip speaks for the chevron as well, so the
            chevron carries one only when nothing outside it does. Two
            tooltips over one control would fight for the same hover and,
            told to stay open, would both be open at once. */}
        {explainsItself ? (
          chevron
        ) : (
          <Tooltip
            content={ariaLabel ?? "More options"}
            {...tooltipProps}
            triggerDisabled={isDisabled}
          >
            {chevron}
          </Tooltip>
        )}
      </div>
    )

    return (
      <DropdownMenuPrimitive.Root>
        {explainsItself ? (
          <Tooltip content={disabledReason} {...tooltipProps} triggerDisabled>
            {control}
          </Tooltip>
        ) : (
          control
        )}
        <DropdownMenuPrimitive.Portal>
          <DropdownMenuPrimitive.Content
            align="end"
            sideOffset={4}
            className={dropdownMenuContentVariants()}
          >
            {items.map((item, i) => (
              <DropdownMenuItem
                key={i}
                disabled={item.disabled}
                onSelect={item.onClick}
                destructive={item.destructive}
                data-testid={item["data-testid"]}
                className="[&_svg]:size-4"
              >
                <span className="flex items-center gap-2">
                  <IconSlot className="flex shrink-0">{item.icon}</IconSlot>
                  {item.label}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuPrimitive.Root>
    )
  },
)

SplitButton.displayName = "SplitButton"

export { SplitButton }
